import { proxy } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { resetStore } from '@/lib/valtio/reset-store'
import {
  useStoreKeyEquals,
  useStoreKeySelector,
} from '@/lib/valtio/store-hooks'

type CueTimingState = {
  /**
   * Id of the next cue after the running one that starts at a fixed time.
   *
   * `null` while there is no runner (`PRESHOW`), after it ends, or when no
   * fixed cue follows.
   */
  nextHardStartCueId: string | null
}

const initialState: CueTimingState = {
  nextHardStartCueId: null,
}

/**
 * Rundown-wide cue timing values that join cue state with the runner.
 *
 * @remarks
 *   Cue rules that read only cue data belong in `cue.derived.ts`, where they
 *   are computed from the source stores. This one searches forward from the
 *   running cue, and the runner is a Jotai atom, so the join cannot be a valtio
 *   derivation. `useRundownTiming` writes it once per input change; rows read
 *   the result instead of running the search themselves.
 */
const cueTimingStore = proxy<CueTimingState>(deepClone(initialState))

export const cueTimingActions = {
  setNextHardStartCueId(cueId: string | null) {
    cueTimingStore.nextHardStartCueId = cueId
  },

  /** Called when leaving a rundown. */
  resetStore() {
    resetStore(cueTimingStore, initialState)
  },
}

// =============== HOOKS

/**
 * Id of the next cue after the running one that starts at a fixed time.
 *
 * Cue re-renders only when the id itself changes, not when any cue doc does.
 */
export function useNextHardStartCueId(): string | null {
  return useStoreKeySelector(
    cueTimingStore,
    'nextHardStartCueId',
    (cueId) => cueId,
  )
}

/**
 * Is this cue the next hard start?
 *
 * Re-renders only when the answer flips for THIS cue, so a move of the hard
 * start touches two cues, not every cue.
 */
export function useIsNextHardStartCue(cueId: string): boolean {
  return useStoreKeyEquals(cueTimingStore, 'nextHardStartCueId', cueId)
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(cueTimingStore, { name: 'cueTimingStore', enabled: true })
}
// #endregion
